import React, { useState, useRef, useEffect } from 'react';
import { Grid, Box, IconButton, Tooltip, Typography, FormGroup, FormControlLabel, Switch } from '@mui/material';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { removeContentEditable, escapeHtml } from '../markdown/markdown-cleanup';
import MessageItem from './MessageItem';
import { useSettingsCheck } from '../settings';
import Editor from '../editor';
import Iconify from '../iconify';
import ChatBoxUsers from './ChatBoxUsers';
import TicketUserAction from '../../components/dashboard/tickets/TicketUserAction';
import { fCurrency } from '../../utils/formatNumber';

const normalizeText = (value) => (value || '').replace(/\s+/g, ' ').trim();

const buildInvoiceItemLineText = (item) => {
  const label = [item.vendorItemId, item.description].filter(Boolean).join(' - ');
  const priceText = `Invoice Price: ${fCurrency(item.price)}`;
  return normalizeText(label ? `${label} | ${priceText}` : priceText);
};

const buildInvoiceItemLineHtml = (item) => {
  const label = [item.vendorItemId, item.description].filter(Boolean).map(escapeHtml).join(' - ');
  const priceText = `Invoice Price: ${fCurrency(item.price)}`;
  return `<li>${label ? `${label} | ${priceText}` : priceText}</li>`;
};

ChatBox.propTypes
 = {
  messagesAndUserActions: PropTypes.array,
  handleSend: PropTypes.func,
  allowedUsers: PropTypes.array,
  chatUsers: PropTypes.array,
  deleteUser: PropTypes.func,
  addUser: PropTypes.func,
  emailChatOn: PropTypes.bool,
  updateEmailChat: PropTypes.func,
  selectedChatItems: PropTypes.array,
  addItemsToChat: PropTypes.bool,
  setAddItemsToChat: PropTypes.func,
};

export default function ChatBox({ messagesAndUserActions, handleSend, allowedUsers, chatUsers, deleteUser, addUser, emailChatOn, updateEmailChat, selectedChatItems, addItemsToChat, setAddItemsToChat }) {
  const signature = useSettingsCheck('AutoSignatureOnTickets');
  const [messageText, setMessageText] = useState(signature?.show ? signature?.value : '');
  const [attachments, setAttachments] = useState([]);
  const [expand, setExpand] = useState(false);
  const [reply, setReply] = useState({});
  const [focusSignal, setFocusSignal] = useState(1);
  const insertedItemBulletTextsRef = useRef(new Set());
  const { data: { invoiceItemData : { invoice } } } = useSelector((state) => state.invoicing);

  function isQuillEmpty(html) {
    const text = html.replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, " ").trim();
    return text === "";
  }

  const headerStyle = {
    display: 'flex',
    px: 1,
    pt: 1,
    borderColor: 'info.main',
    alignItems: 'center',
  };

  const stripHtml = (htmlString) => {
    const parser = new DOMParser();
    // if there is a reply within the reply, remove it
    let text = stripReplyPreview(htmlString);
    // TODO: when implementing replies with tickets, replace img tags with camera icon if using with attachments
    // replace <br> and <p> tags with spaces before stripping html content
    text = text
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/p>/gi, ' ')
      .replace(/<p[^>]*>/gi, ' ');
    const doc = parser.parseFromString(text, 'text/html');
    // get only the text content without html tags
    text = doc.body.textContent || "";
    const decoded = decodeEntities(text || "");
    return decoded.trim();
  }

  // if the reply contains a reply preview, strip it out
  function stripReplyPreview(htmlString) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const replyPreview = doc.querySelector('.reply-preview');
    if (replyPreview) replyPreview.remove();
    return doc.body.innerHTML;
  }

  // handle escape characters
  const decodeEntities = (text) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = text;
    return txt.value;
  };

  const handleReply = (text, user, id) => {
    const strippedText = stripHtml(text); 
    setReply({text: strippedText, id, user});
  }

  const scrollRef = useRef(null);
  const autoExpandRef = useRef(false); 

  useEffect(() => {
    if (!autoExpandRef.current && messagesAndUserActions?.length > 0) {
      setExpand(true);
      autoExpandRef.current = true;
    }

    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
      });
    }
  }, [messagesAndUserActions]);

  useEffect(() => {
    setMessageText((prev) => {
      // Quill re-serializes any HTML we hand it through its own Delta model, which drops
      // custom attributes/wrappers we might use as markers - so previously-inserted item
      // bullets can only be found again by matching their rendered text, not a marker.
      const parser = new DOMParser();
      const doc = parser.parseFromString(prev || '', 'text/html');

      doc.querySelectorAll('li').forEach((li) => {
        const text = normalizeText(li.textContent);
        if (text && insertedItemBulletTextsRef.current.has(text)) {
          const parentList = li.parentElement;
          li.remove();
          if (parentList?.tagName === 'UL' && parentList.children.length === 0) {
            parentList.remove();
          }
        }
      });

      let html = doc.body.innerHTML;

      if (selectedChatItems?.length) {
        selectedChatItems.forEach((item) => insertedItemBulletTextsRef.current.add(buildInvoiceItemLineText(item)));
        const blockHtml = `<ul>${selectedChatItems.map(buildInvoiceItemLineHtml).join('')}</ul>`;
        const defaultMessage = signature?.show ? signature?.value : '';
        if (!html || isQuillEmpty(html)) {
          html = blockHtml;
        } else if (html === defaultMessage) {
          // draft is just the untouched signature - put the items before it, don't discard it
          html = `${blockHtml}${html}`;
        } else {
          html = `${html}${blockHtml}`;
        }
      }

      return html;
    });

    if (selectedChatItems?.length) {
      setExpand(true);
      setFocusSignal((prev) => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChatItems]);

  const stripRemoveButton = (html) => html.replace(/<button[^>]*class="reply-remove"[^>]*>.*?<\/button>/gi, '');

  const addAttachment = (event) => {
    const selectedFiles = Array.from(event?.target?.files || []);
    if (selectedFiles.length === 0) return;

    setAttachments((prevAttachments) => {
      const newFiles = selectedFiles.filter(
        (file) => !prevAttachments.some((prev) => prev.name === file.name && prev.size === file.size)
      );
      return [...prevAttachments, ...newFiles];
    });
  };

  const removeFile = (fileToRemove) => () => {
    setAttachments((prevAttachments) => prevAttachments.filter((file) => file !== fileToRemove));
  };

  return (
    <Grid>
      <Box sx={headerStyle}>
        <Typography color="info.main" sx={{ fontSize: '15px', fontWeight: 'bold', alignContent: 'center', mr: 1 }}>
          Chat
        </Typography>
        <ChatBoxUsers users={chatUsers} allUsers={allowedUsers} deleteUser={deleteUser} addUser={addUser}  />
        <Tooltip title={`${expand ? 'less' : 'more'}`}>
          <IconButton
            onClick={() => setExpand(!expand)}
            sx={{ color: 'info.main', padding: 0, height: 'fit-content', mt: 0.5, ml:'auto' }}
          >
            <Iconify icon={`ic:twotone-expand-${expand ? 'less' : 'more'}`} width={26} height={26} />
          </IconButton>
        </Tooltip>
      </Box>
      {expand && (
        <>
          <Box>
            <Box sx={{ overflowY: 'auto', minHeight: '10vh', maxHeight: '25vh', my: 1 }} ref={scrollRef}>
            {messagesAndUserActions.map((item, idx) => (
              item.type === 'message' ? <MessageItem
                key={idx}
                onReply={handleReply}
                reply={reply}
                messages={messagesAndUserActions}
                message={{
                  fullName: item.user.fullName,
                  id: item.id,
                  note: removeContentEditable(item.extraInfo ?? ''),
                  sentBy: item.user.email,
                  sentOn: item.dateAdded,
                  parentCommentId: item.parentCommentId,
                  attachments: item.attachments ?? [],
                }}
              /> : <TicketUserAction key={idx} item={item} idx={idx} />
            ))}
            </Box>
            <Editor
              id="ticket-reply"
              value={messageText}
              placeholder="Type a message"
              sx={{
                borderColor: 'transparent',
                flexShrink: 0,
              }}
              handleSend={() => {
                if (!messageText || isQuillEmpty(messageText)) return;
                handleSend(stripRemoveButton(messageText), reply?.id, attachments);
                setMessageText(signature?.show ? signature?.value : '');
                setAttachments([]);
                setAddItemsToChat?.(false);
              }}
              onChange={(e) => setMessageText(e)}
              handleFileInputChange={addAttachment}
              attachments={attachments}
              removeFile={removeFile}
              attachFile
              sendIcon
              users={allowedUsers}
              replyMessage={reply}
              onReplyRemove={() => setReply({})}
              chatBoxEditor
              shouldFocus={focusSignal}
              addItemsToTicket={addItemsToChat}
              setAddItemsToTicket={setAddItemsToChat}
              addItemsLabel="Add items to chat"
            />
          </Box>
          <Box>
            <FormGroup>
              <FormControlLabel
                label={
                  <Tooltip
                    placement="top"
                    arrow
                    title="Email updates to all users when a message is sent"
                  >
                    <span>Email updates</span>
                  </Tooltip>
                }
                labelPlacement="start"
                control={
                  <Switch
                    checked={emailChatOn}
                    onChange={() => updateEmailChat(!emailChatOn)}
                    size="small"
                    disabled={!invoice || invoice?.editingLocked}
                  />
                }
                sx={{
                  m: 0,
                  width: 1,
                }}
              />
            </FormGroup>
          </Box>
        </>
      )}
    </Grid>
  );
}
